import { Anchor, Box, Button, Card, Code, Grid, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconExternalLink } from '@tabler/icons-react';
import { codes as countriesAndCodes } from 'country-calling-code';
import currencyCodes from 'currency-codes/data';
import ISO6391 from 'iso-639-1';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import ErrorMessage from '../components/ErrorMessage';
import Metadata from '../components/Metadata';
import SearchableCombobox from '../components/SearchableCombobox';
import platforms from '../constants/platforms';
import shopCategories from '../constants/shopCategories';
import { getDefaultQuietHourConfig } from '../helpers/ScopeHelper';
import useFetch from '../helpers/useFetch';
import { validateUrlProtocol } from '../helpers/validateURL';
import { setUser } from '../store/authSlice';

const DomainCreatePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetch = useFetch();
  const { accountId } = useParams();
  const { user } = useSelector((state) => state.auth);

  const [domain, setDomain] = useState({
    ...getDefaultQuietHourConfig(),
    subscriptionManagement: 'internal',
    subscriptionRequestBehavior: 'subscribe',
  });
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [isInvalidUrl, setIsInvalidUrl] = useState(false);

  const handleChange = useCallback((e) => {
    setDomain((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handlePlatformChange = useCallback((value) => {
    setDomain((prev) => ({ ...prev, platform: value }));
  }, []);

  const handleCountryChange = useCallback((value) => {
    setDomain((prev) => ({ ...prev, country: value }));
  }, []);

  const handleLocaleChange = useCallback((value) => {
    setDomain((prev) => ({ ...prev, locale: value }));
  }, []);

  const handleCurrencyChange = useCallback((value) => {
    setDomain((prev) => ({ ...prev, currency: value }));
  }, []);

  const handleCategoryChange = useCallback((value) => {
    setDomain((prev) => ({ ...prev, category: value }));
  }, []);

  const allowCreateDomain = domain.platform !== 'shopify';

  const submitForm = (e) => {
    e.preventDefault();
    setError(undefined);
    setLoading(true);
    fetch(`/api/admin/accounts/${accountId}/domains`, {
      method: 'POST',
      body: JSON.stringify(domain),
    })
      .then((result) => {
        fetch(`/api/admin/sessions/current`)
          .then((res) => {
            dispatch(setUser(res.user));
            // A new shop starts on its Brand Identity step, which reads the website right away.
            navigate(`/accounts/${accountId}/domains/${result.domain.id}/confirm/brandContext`);
          })
          .catch(setError);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  };

  const accountOptions = useMemo(
    () =>
      user.userRoles
        .filter((role) => role.account)
        .map((role) => ({
          label: role.account.name,
          value: role.account.id.toString(),
        })),
    [user.userRoles]
  );

  const currencyOptions = useMemo(
    () =>
      currencyCodes.map((data) => ({
        label: `${data.currency} (${data.code})`,
        value: data.code,
      })),
    [],
  );

  const countryOptions = useMemo(
    () =>
      countriesAndCodes.map((c) => ({
        label: `+${c.countryCodes}  ${c.country}`,
        value: c.country,
      })),
    [],
  );

  const languageOptions = useMemo(
    () =>
      ISO6391.getLanguages(ISO6391.getAllCodes()).map((locale) => ({
        label: locale.name,
        value: locale.code,
      })),
    [],
  );

  const categoryOptions = useMemo(() => shopCategories.map((c) => ({ label: c, value: c })), []);

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'auto' }}>
      <Metadata title="Create Shop" />
      <Grid align="flex-start" justify="center" py="lg" px="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Text size="xl" fw={700} mb="md">
            Create Shop
          </Text>
          <Card withBorder p="lg">
            <form onSubmit={(e) => submitForm(e)}>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Account</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={accountOptions}
                    value={accountId}
                    onChange={(value) => navigate(`/accounts/${value}/domains/new`, { replace: true })}
                    placeholder="Select account"
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm" ta="left">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Name</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="name"
                    placeholder="My Awesome Shop"
                    value={domain.name || ''}
                    error={domain.name !== undefined && domain.name === '' ? 'name is required' : null}
                    onChange={(e) =>
                      handleChange({
                        target: { name: 'name', value: e.target.value },
                      })
                    }
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>URL</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="url"
                    placeholder="https://myawesomeshop.com"
                    value={domain.url || ''}
                    error={
                      isInvalidUrl ? 'The URL field is required. Please provide a URL with a valid protocol.' : null
                    }
                    onChange={(e) => {
                      handleChange({
                        target: { name: 'url', value: e.target.value },
                      });
                      setIsInvalidUrl(!validateUrlProtocol(e.target.value));
                    }}
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Platform</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={platforms}
                    value={domain.platform}
                    onChange={handlePlatformChange}
                    placeholder="Type to search..."
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Country</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={countryOptions}
                    value={domain.country}
                    onChange={handleCountryChange}
                    placeholder="Type to search..."
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Primary Language</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={languageOptions}
                    value={domain.locale}
                    onChange={handleLocaleChange}
                    placeholder="Type to search..."
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Primary Currency</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={currencyOptions}
                    value={domain.currency}
                    onChange={handleCurrencyChange}
                    placeholder="Type to search..."
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Shop Category</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    options={categoryOptions}
                    value={domain.category}
                    onChange={handleCategoryChange}
                    placeholder="Type to search..."
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Email Sender Address</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="senderEmail"
                    placeholder="info@shop.com"
                    value={domain.senderEmail || ''}
                    error={
                      domain.senderEmail !== undefined && domain.senderEmail === ''
                        ? 'sender email is required'
                        : null
                    }
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: 'senderEmail',
                          value: e.target.value,
                        },
                      })
                    }
                  />
                </Grid.Col>
              </Grid>
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={500}>Subscription Management</Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    value={domain.subscriptionManagement ?? ''}
                    onChange={(subscriptionManagement) =>
                      handleChange({
                        target: {
                          name: 'subscriptionManagement',
                          value: subscriptionManagement,
                        },
                      })
                    }
                    data={[
                      { label: 'External', value: 'external' },
                      { label: 'Internal', value: 'internal' },
                    ]}
                  />
                  <Text size="sm" c="dimmed">
                    If you keep information in your shop about whether a user is subscribed or not, select{' '}
                    <strong>External</strong>. Otherwise, select <strong>Internal</strong>.<br />
                    In <strong>Internal</strong> mode, you should only send email and SMS communication through Releva
                    because unsubscribes would not propagate to other third-party systems!
                  </Text>
                </Grid.Col>
              </Grid>
              {domain.subscriptionManagement === 'external' ? (
                <Grid align="center" mb="sm">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text fw={500}>Email Unsubscribe URL</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <TextInput
                      name="unsubscribeLink"
                      placeholder="http://myawesomeshop.com/unsubscribe"
                      value={domain.unsubscribeLink || ''}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: 'unsubscribeLink',
                            value: e.target.value,
                          },
                        })
                      }
                    />
                    <Text size="sm" c="dimmed">
                      You need to implement this url in your shop. We will send users to this link with
                      ?encodedEmail=base64(email)&url=(shopUrl).
                      <br />
                      When a user visits this link, you need to mark the user as unsubscribed in your database, and
                      also send an unsubscribe API call to Releva as described{' '}
                      <Anchor
                        href="https://releva.ai/documentation/gdpr-identity-apis/unsubscribe/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        <IconExternalLink size={14} /> in the docs
                      </Anchor>
                      . <br />
                      <strong>Note:</strong> Leave this empty to rely on per-profile unsubscribe links instead: each
                      profile then needs its own <Code>unsubscribeLink</Code>, sent with the profile upsert. Profiles
                      with no link of their own are skipped for Opt-in Marketing and Opt-out Marketing campaigns, though
                      transactional campaigns still reach them.
                      <br />
                      Fill the URL in if you want a single shop-wide page to serve as the fallback for profiles that
                      have no link of their own.
                    </Text>
                  </Grid.Col>
                </Grid>
              ) : (
                ''
              )}
              <Box display="flex" style={{ justifyContent: 'flex-end' }}>
                <Button
                  disabled={
                    loading ||
                    !domain.name ||
                    !domain.senderEmail ||
                    !validateUrlProtocol(domain.url) ||
                    !domain.currency ||
                    !domain.platform ||
                    !allowCreateDomain
                  }
                  type="submit"
                >
                  <IconDeviceFloppy size={16} /> {loading ? 'Loading...' : 'Create'}
                </Button>
              </Box>
            </form>
          </Card>
          {error && <ErrorMessage message={error.message} />}
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default DomainCreatePage;
